import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { isEmpty } from 'underscore';
import { CaptionsConfig } from 'blocks/TextOverlayModal/v2';
import {
  getBaseCaptionsOverride,
  isCaptionsAnimationsTabDisabled,
} from 'utils/embed/captions/utils';
import { parseSearch } from 'utils/routes';
import { useEditorState } from '../context/VideoEditorStateContext';
import { useTemplateDispatch } from '../context/VideoTemplateDispatchContext';
import { useTemplateState } from '../context/VideoTemplateStateContext';
import { useModalManager } from '../ModalManager';
import { getCaptionsFromConfig } from '../state';
import { CaptionsChildViewContextType } from './CaptionsChildViewContext/types';
import {
  CAPTIONS_MANAGEMENT_CHILD_VIEW_PROPS,
  DEFAULT_CHILD_VIEW_PROPS,
  DEFAULT_CHILD_VIEW_PROPS_VALUES,
  INCLUDE_CAPTIONS_CHILD_VIEW_PROPS,
} from './constants';
import useCaptionsDefaultStyle from './useCaptionsDefaultStyle';
import useCaptionsLockedFeatures from './useCaptionsLockedFeatures';
import useHandleTranscribe from './useHandleTranscribe';

export const ANIMATIONS_NOT_SUPPORTED_MSG = 'Not supported for Full Episode';

export default function useCaptionsChildViewProps(): CaptionsChildViewContextType {
  const { aspectRatio, captions, transcription } = useTemplateState();
  const { isFullEpisode, allowCaptionsManagement } = useEditorState();
  const lockedFeatures = useCaptionsLockedFeatures();

  const {
    media,
    isLoadingMediaInfo,
    isTranscribing,
    transcribingProgress,
    eddyProjectCuid,
    startTranscribeProcess,
    stopTranscribeProcess,
  } = useHandleTranscribe();
  const dispatch = useTemplateDispatch();
  const modalManager = useModalManager();
  const location = useLocation();

  useCaptionsDefaultStyle({
    aspectRatio,
    isFullEpisode,
    currentCaptionsConfig: captions,
  });

  const query = parseSearch(location.search);
  const wizard = query.type;

  const propsByWizard = useMemo((): CaptionsChildViewContextType => {
    let propsByWizardType: CaptionsChildViewContextType = {};

    if (allowCaptionsManagement) {
      return CAPTIONS_MANAGEMENT_CHILD_VIEW_PROPS;
    }

    switch (wizard) {
      case 'autovideo': {
        propsByWizardType = INCLUDE_CAPTIONS_CHILD_VIEW_PROPS;

        break;
      }

      case 'audiogram': {
        propsByWizardType = INCLUDE_CAPTIONS_CHILD_VIEW_PROPS;

        break;
      }

      case 'video-transcription': {
        propsByWizardType = DEFAULT_CHILD_VIEW_PROPS;

        break;
      }

      case 'episode': {
        propsByWizardType = CAPTIONS_MANAGEMENT_CHILD_VIEW_PROPS;

        break;
      }

      default: {
        propsByWizardType = DEFAULT_CHILD_VIEW_PROPS_VALUES;

        break;
      }
    }

    return propsByWizardType;
  }, [allowCaptionsManagement, wizard]);

  const handleTranscriptionChange = useCallback((): void => {
    const nextTranscribeValue = !transcription?.transcribe;

    if (
      nextTranscribeValue &&
      propsByWizard?.startTranscribingWhenEnablingCaptions
    ) {
      startTranscribeProcess();
    }

    if (!nextTranscribeValue) {
      stopTranscribeProcess();
    }

    const baseCaptionsOverride = !isEmpty(captions)
      ? captions
      : getCaptionsFromConfig(
          getBaseCaptionsOverride(aspectRatio, isFullEpisode),
        );

    dispatch({
      type: 'TRANSCRIPTION_CHANGE',
      payload: {
        transcription: {
          ...transcription,
          transcribe: nextTranscribeValue,
        },
      },
    });

    dispatch({
      type: 'CAPTIONS_CONFIG_UPDATE',
      payload: {
        captions: nextTranscribeValue
          ? baseCaptionsOverride
          : ({} as CaptionsConfig),
      },
    });
  }, [
    aspectRatio,
    captions,
    dispatch,
    isFullEpisode,
    propsByWizard?.startTranscribingWhenEnablingCaptions,
    startTranscribeProcess,
    stopTranscribeProcess,
    transcription,
  ]);

  const handleEditCaptionsStyle = useCallback((): void => {
    const showTabDisabled = isCaptionsAnimationsTabDisabled(isFullEpisode);

    modalManager.showModal('edit-caption-style', {
      animationsTabEnabled: true,
      animationsTabDisplayConfig: {
        showTabDisabled,
        tabTooltip: showTabDisabled && ANIMATIONS_NOT_SUPPORTED_MSG,
      },
      captionsOverride: captions,
      lockedFeatures,
      localPresets: {
        enabled: true,
        presets: [
          {
            type: 'reset',
            variant: isFullEpisode ? 'captions-full-ep' : 'captions-clip',
          },
          !isFullEpisode && {
            type: 'old-style',
          },
        ],
      },
      onSubmit: (captionsConfig: CaptionsConfig) => {
        dispatch({
          type: 'CAPTIONS_CONFIG_UPDATE',
          payload: {
            captions: captionsConfig,
          },
        });
      },
    });
  }, [captions, dispatch, isFullEpisode, lockedFeatures, modalManager]);

  const handleEditCaptionText = useCallback((): void => {
    modalManager.showModal('edit-caption-text', {
      eddyProjectId: eddyProjectCuid,
    });
  }, [eddyProjectCuid, modalManager]);

  return {
    ...propsByWizard,
    media,
    isLoadingMediaInfo,
    isTranscribing,
    transcribingProgress,
    eddyProjectCuid,
    onTranscriptionChange: handleTranscriptionChange,
    onEditCaptionsStyle: handleEditCaptionsStyle,
    onEditCaptionText: handleEditCaptionText,
  } as CaptionsChildViewContextType;
}
